import { Bar, XAxis, YAxis, BarChart as RechartsBar, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { useTranslation } from '@klv/hooks/useTranslation';
import ChartXTick from '@klv/atoms/chart-x-tick';
import { barChartColors as c, formatValue } from '@klv/shared/utils/charts';
import type { IBarChart } from './types';
import { clampSize } from '@klv/shared/utils/functions';
import { useWindowDimensions } from '@klv/hooks/useWindowDimensions';
import { designSystemColors } from '@klv/shared/styles/colors';
import * as s from './styles/barchart.module.css';

const BarChart = ({ data, headers, layout = 'horizontal', backgroundColor, numberFormat }: IBarChart) => {
  const { i18n } = useTranslation();
  const { width } = useWindowDimensions(); 
  const isVertical = layout === 'vertical';
  const isMobile = width <= 768;
  const isDesktop = width >= 1366;
  const textColor = ['white', 'lightSand'].includes(backgroundColor) ? designSystemColors.charcoal : designSystemColors.white;
  const fontSizeClamp = clampSize(2, 2.4);
  const isScrollable = isMobile && layout === 'horizontal';

  // how wide should the label be?
  function getLabelWidth() {
    if ( width <= 576) {
      return 125;
    }
    if (isMobile) {
      return 150;
    }

    return 200;
  }

  // set a decent height based on number of rows in our data and a base height.
  function getContainerHeight() {
    const multiplier = Math.max(1, (data.length - 1));
    return (multiplier * 60) + 250;
  }

  if (layout === 'horizontal' && data.length === 2) {
    const parsedResult = Object.fromEntries(headers.map(value => [value, '']));
    data.unshift(parsedResult);
    data.push(parsedResult);
  }

  const getMinWidth = () => {
    if (isScrollable) {
      return 492;
    } else if (isVertical && isDesktop) {
      return 300;
    }
    return undefined;
  };

  const CustomYAxisTick = ({ y, payload }: {
    y: number;
    payload: { value: string; };
  }) => {
    const axisColor = backgroundColor === 'charcoal' ? designSystemColors.white : designSystemColors.charcoal;
    return (
      <g transform={`translate(${0}, ${y - 30})`}>
        <foreignObject x={0} y={0} width={getLabelWidth()} height={60} style={{ overflow: 'visible' }}>
          <div className={s.customYAxisTick} data-value={payload?.value}
            style={{ '--axis-color': axisColor } as React.CSSProperties} />
        </foreignObject>
      </g>
    );
  };

  return (
    <div style={{ width: '100%', overflowX: isScrollable ? 'auto' : 'hidden', ...(isScrollable && { scrollBehavior: 'smooth', paddingBottom: '1rem' }) }}>
      <ResponsiveContainer width="100%" height={getContainerHeight()} minWidth={getMinWidth()}>
        <RechartsBar data={data} layout={layout}>
          {isVertical ? (
            <>
              <YAxis 
                width={getLabelWidth()} 
                type={'category'} 
                dataKey={headers[0]} 
                axisLine={false} 
                tickLine={false} 
                fill={textColor} 
                tick={props => <CustomYAxisTick {...props}/>}/>
                <XAxis type='number' dataKey={undefined} axisLine={false} fill={textColor} tickLine={false} tick={false} />
            </>
          ) : (
            <XAxis type='category' dataKey={headers[0]} axisLine={false} fill={textColor} tickLine={false} tick={props => <ChartXTick {...props} backgroundColor={backgroundColor} fontSizeClamped={fontSizeClamp} fontWeight='normal' />} />
          )}
          
          {headers.slice(1).map((key: string, i: number) => (
            <Bar
              stackId='a'
              key={key}
              dataKey={key}
              fill={c[i]}
              barSize={isVertical ? 60 : undefined}
            >
              <LabelList
                dataKey={key}
                fontFamily="Sohne"
                fill={designSystemColors.charcoal}
                fontSize={20}
                formatter={(value: any) => (value !== '' ? `${formatValue(numberFormat, value, i18n.language)}` : '')}
                position="inside"
              />
            </Bar>
          ))}
          <Legend
            layout='vertical'
            verticalAlign={isDesktop && !isVertical ? 'middle' : 'bottom'}
            align={isDesktop && !isVertical ? 'left' : 'center'}            
            iconSize={20}
            iconType='square'
            style={{ marginTop: '200px' }}
            formatter={(value: string) => (
              <span style={{ color: textColor, display: 'inline-block', paddingTop: 20, fontSize: 16 }}>
                {value}
              </span>
            )}
          />
        </RechartsBar>
      </ResponsiveContainer>
    </div>
  );
};

export default BarChart;
